import { useState } from "react";
import { speakPhonemes, stopEspeak } from "../../speak/speakPhonemes";
import { ColumnAlign } from "../widgets/ColumnAlign";
import { IpaKeyboard } from "../widgets/IpaKeyboard";
import { IpaButtons } from "../widgets/IpaButtons";
import { ConversionRules } from "../ortho/conversionRules";
import { sampleRules } from "../ortho/sampleRules";
import { KeyToOrthoMapping } from "../ortho/keyToOrthoMapping";
import { sampleKeysMaps } from "../ortho/sampleKeyMaps";
import { loadChineseToPinyin } from "../../loader/loadChineseToPinyin";
import { ch_to_pinyin } from "../../transcribe/ch_to_pinyin";
import { Say } from "../widgets/Say";

const roughWidth = 500;   /** when an explicit width is needed for content */
const cols = 70;
const samplePinyin = 'nǐ hǎo';

const descriptions: Record<string, string> = {
  "\u0304": 'high tone - ā',
  "\u0301": 'rising tone - á',
  "\u030c": 'falling-rising tone - ǎ',
  "\u0300": 'falling tone - à',
  E: "capital e",
  ê: "short e",
  Ê: "capital ê",
  U: "capital u",
  ü: "like German ü or French y",
  Ü: "capital ü",
};

/** UI that converts Pinyin or Chinese symbols to IPA & can then speak it */
export function ConvertMandarin() {
  const [converter] = useState(getConverter());
  const [keymaps] = useState(getKeyMappings());
  const [inputText, setInputText] = useState(samplePinyin);
  const [outputText, setOutputText] = useState(converter.convert(samplePinyin));
  const [insert, setInsert] = useState<[string] | undefined>();
  // eslint-disable-next-line
  const [_] = useState(loadChineseToPinyin());

  /** user changed the input text */
  function changeInputText(newText: string) {
    setInputText(newText);
    const pinyin = toPinyin(newText);
    const results = converter.convert(pinyin);
    setOutputText(results);
  }

  function speakOutput() {
    speakPhonemes(outputText, undefined, converter.getAddPhonemeLength());
  }

  return (<>
    <IpaButtons onClick={(s) => setInsert([s])} width={400}
      single={keymaps.getSingle()} multi={keymaps.getMulti()} descriptions={(s) => descriptions[s]} />
    <IpaKeyboard cols={cols} rows={5} value={inputText}
      single={keymaps.getSingle()} multi={keymaps.getMulti()}
      insert={insert} onChange={changeInputText} onSpeak={speakOutput} />
    <br />

    <ColumnAlign width={roughWidth}>{[
      <span key={1}></span>,
      <span key={2}>
        <button onClick={() => speakOutput()}>speak output</button>
        &nbsp;&nbsp;&nbsp;
        <button onClick={() => stopEspeak()}>stop</button>
      </span>,
    ]}</ColumnAlign>

    <IpaKeyboard cols={cols} rows={5} value={outputText}
      onChange={setOutputText} onSpeak={speakOutput} />
  </>);
}

/** a gadget that speaks Mandarin using either Pinyin or Chinese characters */
export function SayMandarin(props: { children: string }) {
  const color = 'rgb(76,13,77)';
  const pinyin = toPinyin(props.children);
  const ipa = getConverter().convert(pinyin);
  const say = `[${ipa}]`;
  return <Say say={say} textColor={color}>{props.children}</Say>
}

function getConverter() {
  if (converter === null) {
    converter = new ConversionRules();
  }
  converter.setRuleSet(sampleRules['Pinyin']);
  return converter;
}
let converter: ConversionRules | null = null;

function getKeyMappings() {
  const keys = new KeyToOrthoMapping();
  keys.setAll(sampleKeysMaps['Pinyin']);
  return keys;
}

/** convert Chinese symbols to pinyin, preserving all other symbols */
function toPinyin(s: string): string {
  if (!quickCheck(s)) {
    return s;
  }
  let result = '';
  for (const c of s) {
    const p = ch_to_pinyin[c];
    result += p === undefined ? c : p + ' ';
  }
  return result;
}
function quickCheck(s: string): boolean {
  // quick filter to detect Chinese symbols
  for (let i = 0; i < s.length; i++) {
    if (s.charCodeAt(i) >= 0x4e00) {
      return true;
    }
  }
  return false;
}
